import React, { useCallback, useEffect } from 'react';
import { AGILITY_BACKLOG } from '@atlassian/jira-common-constants/src/analytics-sources.tsx';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/main.tsx';
import { useIsAnyAppLoading } from '@atlassian/jira-forge-action-apps-controller/src/index.tsx';
import {
	ForgeAppsRuntimeContainer,
	type ForgeAppRenderer,
} from '@atlassian/jira-forge-action-apps-controller/src/runtime-container.tsx';
import { getAnalyticsAttributesFromExtension } from '@atlassian/jira-forge-ui-analytics/src/common/utils/get-analytics-attributes-from-extension/index.tsx';
import { ViewAnalyticsWrapper } from '@atlassian/jira-forge-ui-analytics/src/ui/backlog-action/index.tsx';
import { AsyncBacklogAction } from '@atlassian/jira-forge-ui-async/src/ui/backlog-action/async.tsx';
import type { BacklogActionExtensionData } from '@atlassian/jira-forge-ui-types/src/common/types/extension-data.tsx';
import type { BacklogAction } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';

const BacklogActionAppRenderer: ForgeAppRenderer<BacklogAction, BacklogActionExtensionData> = ({
	app,
	setLoaded,
	localId,
	handleError: baseHandleError,
	closeApp,
}) => {
	// TODO: require further review and touch up with UX team
	// .     https://ecosystem-platform.atlassian.net/browse/EXT-2566
	// [1] handle background tasks and loading state
	// useEffect(() => {
	// 	setTimeout(setLoaded, 10000);
	// 	return setLoaded;
	// }, [setLoaded]);
	//

	// force render the default error message (instead of the content from the error)
	const handleError = useCallback(() => {
		baseHandleError && baseHandleError();
	}, [baseHandleError]);

	return (
		<ViewAnalyticsWrapper
			attributes={{
				source: AGILITY_BACKLOG,
				...getAnalyticsAttributesFromExtension(app.extension),
			}}
		>
			<AsyncBacklogAction
				localId={localId}
				extension={app.extension}
				extensionData={app.extensionData}
				onClose={closeApp}
				analyticsAttributes={{ source: AGILITY_BACKLOG }}
				onLoad={setLoaded}
				onInitialRender={setLoaded}
				onError={handleError}
			/>
		</ViewAnalyticsWrapper>
	);
};

type BacklogActionForgeRuntimeContainerProps = {
	onAppsFinishLoading?: () => void;
};

const BacklogActionForgeRuntimeContainer: React.FC<BacklogActionForgeRuntimeContainerProps> = ({
	onAppsFinishLoading,
}) => {
	const [isAnyForgeAppLoading] = useIsAnyAppLoading();

	useEffect(() => {
		// this is to close the dropdown when all forge apps are loaded
		if (onAppsFinishLoading && !isAnyForgeAppLoading) {
			onAppsFinishLoading();
		}
	}, [isAnyForgeAppLoading, onAppsFinishLoading]);

	return <ForgeAppsRuntimeContainer forgeAppRenderer={BacklogActionAppRenderer} />;
};

export const BacklogActionForgeRuntimeContainerWithErrorBoundary: React.FC<
	BacklogActionForgeRuntimeContainerProps
> = ({ onAppsFinishLoading }) => {
	return (
		<ErrorBoundary packageName="software" id="forge-backlog-action-runtime-container">
			<BacklogActionForgeRuntimeContainer onAppsFinishLoading={onAppsFinishLoading} />
		</ErrorBoundary>
	);
};
